'use client';
import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { getBestPerLanguage, type LanguageBest } from '@/data/localScores';
import { LANGUAGES, TEST_DURATIONS, type LanguageCode } from '@/data/wordLists';
import { useTestConfig } from '@/context/TestConfigContext';
import { smoothScrollToTop } from '@/utils/scroll';

function langMeta(code: string) {
  return LANGUAGES.find((l) => l.code === code);
}

function durLabel(d: number): string {
  return TEST_DURATIONS.find((t) => t.value === d)?.label ?? `${d}s`;
}

function formatDate(iso: string): string {
  const dt = new Date(iso);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[dt.getMonth()]} ${dt.getDate()}`;
}

export default function LeaderboardPreview() {
  const { setLanguage } = useTestConfig();
  const [bests, setBests] = useState<LanguageBest[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setBests(getBestPerLanguage());
    setLoaded(true);
  }, []);

  const scrollToTest = () => smoothScrollToTop();
  const pick = (code: LanguageCode) => {
    setLanguage(code);
    smoothScrollToTop();
  };

  const topWpm = bests.length ? Math.max(...bests.map((b) => b.wpm)) : 0;
  const bestAcc = bests.length ? Math.max(...bests.map((b) => b.accuracy)) : 0;

  const summary = [
    { label: 'Best WPM', value: String(topWpm) },
    { label: 'Best accuracy', value: `${bestAcc.toFixed(1)}%` },
    { label: 'Languages', value: `${bests.length}/${LANGUAGES.length}` },
  ];

  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
      <div className="section-head flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <div>
          <span className="eyebrow">Your progress</span>
          <h2 className="text-2xl md:text-3xl text-foreground mt-2">Your best scores</h2>
        </div>
        <button
          type="button"
          onClick={scrollToTest}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:gap-2.5 transition-all"
        >
          Take a test <ArrowRight size={14} />
        </button>
      </div>

      {/* Loading skeleton (first client paint) */}
      {!loaded && <div className="h-44 animate-pulse" style={{ background: 'var(--muted)' }} />}

      {/* Empty state */}
      {loaded && bests.length === 0 && (
        <div className="py-12 text-center" style={{ border: '1px solid var(--border)' }}>
          <h3 className="font-serif text-xl text-foreground" style={{ fontWeight: 600 }}>
            No scores yet
          </h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
            Take a test in any language and your best WPM will appear here — one personal record per
            language.
          </p>
          <button
            type="button"
            onClick={scrollToTest}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm mt-6"
          >
            Start a typing test
          </button>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8 px-6">
            {LANGUAGES.map((l) => (
              <button
                key={`empty-lang-${l.code}`}
                type="button"
                onClick={() => pick(l.code)}
                className="lang-chip"
              >
                <span>{l.flag}</span>
                <span>{l.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Populated */}
      {loaded && bests.length > 0 && (
        <>
          {/* Summary figures, split by vertical hairlines */}
          <dl
            className="grid grid-cols-3"
            style={{
              borderTop: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            {summary.map((s, i) => (
              <div
                key={s.label}
                className="py-5 px-5 first:pl-0"
                style={i > 0 ? { borderLeft: '1px solid var(--border)' } : undefined}
              >
                <dd
                  className="font-serif stat-value text-foreground leading-none"
                  style={{ fontSize: '1.9rem', fontWeight: 600, letterSpacing: '-0.02em' }}
                >
                  {s.value}
                </dd>
                <dt className="eyebrow mt-2.5">{s.label}</dt>
              </div>
            ))}
          </dl>

          {/* Per-language ranking as a table of records */}
          <div className="mt-2">
            {bests.map((b, idx) => {
              const meta = langMeta(b.language);
              const isTop = idx === 0;
              const pct = topWpm > 0 ? Math.max((b.wpm / topWpm) * 100, 4) : 0;
              return (
                <button
                  key={`best-${b.language}`}
                  type="button"
                  onClick={() => pick(b.language)}
                  className="w-full text-left group py-4 transition-colors hover:bg-muted"
                  style={{ borderBottom: '1px solid var(--border)' }}
                >
                  <div className="flex items-center justify-between gap-4 px-1">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl leading-none shrink-0">{meta?.flag ?? '🌐'}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate flex items-center gap-2">
                          {meta?.label ?? b.language}
                          {isTop && <span className="eyebrow">Top</span>}
                        </div>
                        <div className="text-xs text-faint mt-1">
                          {b.accuracy.toFixed(1)}% accuracy · {durLabel(b.duration)} ·{' '}
                          {formatDate(b.timestamp)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-1.5 shrink-0">
                      <span
                        className="font-mono stat-value text-foreground leading-none"
                        style={{ fontSize: '1.35rem' }}
                      >
                        {b.wpm}
                      </span>
                      <span className="eyebrow">wpm</span>
                    </div>
                  </div>
                  {/* Relative-speed rule — the only chart this list needs */}
                  <div className="mt-3 h-px w-full mx-1" style={{ background: 'var(--border)' }}>
                    <div
                      className="h-px transition-[width] duration-700"
                      style={{ width: `${pct}%`, background: 'var(--foreground)' }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Quick-switch chips */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="eyebrow mr-1">Switch</span>
            {LANGUAGES.map((l) => (
              <button
                key={`quick-${l.code}`}
                type="button"
                onClick={() => pick(l.code)}
                className="lang-chip"
              >
                <span>{l.flag}</span>
                <span>{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
